import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { LockKey, CheckCircle, ShoppingCart, ArrowLeft, Spinner, Tag, X, Clock } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { getReferrerId, getReferrerCapturedAt, setReferrerCode, clearReferrer } from "@/lib/referral";
import { getLastGrade, setLastGrade } from "@/lib/gradePref";
import { toast } from "sonner";
import { usePillars, pillarChipStyle } from "@/contexts/PillarsContext";
import { useI18n } from "@/contexts/I18nContext";

export default function CatalogPage() {
  const { user } = useAuth();
  const { lang } = useI18n();
  const { labelOf, colorOf, settings } = usePillars();
  const [courses, setCourses] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(null);
  const [resolved, setResolved] = useState(null); // { valid, instructor_name, discount_percent, kind, code }
  const [codeInput, setCodeInput] = useState("");
  const [codeErr, setCodeErr] = useState("");
  const [checkoutFor, setCheckoutFor] = useState(null);
  const nav = useNavigate();

  const resolve = async (code) => {
    setCodeErr("");
    try {
      const { data } = await api.get(`/referral/resolve`, { params: { code } });
      if (data.valid) { setResolved(data); setReferrerCode(data.code); return true; }
      setCodeErr(data.message || "That code isn't valid.");
      return false;
    } catch (e) { setCodeErr(formatApiError(e)); return false; }
  };

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/courses", { params: lang !== "en" ? { lang } : {} }); setCourses(data); }
      catch (e) { setErr(formatApiError(e)); }
    })();
    const existing = getReferrerId();
    if (existing) resolve(existing);
    // eslint-disable-next-line
  }, [lang]);

  const canBuy = user?.kind === "user" && user?.role === "public_student";
  const roleSet = new Set([user?.role, ...(user?.roles || [])]);
  const isStaff = ["super_admin", "school_board_admin", "school_admin", "teacher"].some(r => roleSet.has(r));
  const GRADES = ["G7", "G8", "G9", "G10", "G11", "G12", "DEMO"];
  const discount = resolved?.valid ? (resolved.discount_percent || 0) : 0;
  const discountCents = resolved?.valid ? (resolved.discount_cents || 0) : 0;
  const priced = (cents) => Math.max(0, (cents || 0) * (1 - discount / 100) - discountCents);
  const cur = settings?.currency || "CAD";
  const money = (cents) => `$${((cents || 0) / 100).toFixed(2)} ${cur}`;

  const redeemVoucher = async (code) => {
    setCodeErr("");
    try {
      const { data } = await api.post("/site/voucher/redeem", { code });
      toast.success(data.already ? "That code is already on your account" : "Access code accepted — course unlocked! 🎉");
      setCheckoutFor(null); setCodeInput("");
      const { data: cs } = await api.get("/courses", { params: lang !== "en" ? { lang } : {} });
      setCourses(cs);
    } catch (e) { setCodeErr(formatApiError(e)); }
  };

  const applyCode = async (e) => {
    e.preventDefault();
    const c = codeInput.trim();
    if (!c) return;
    if (/^E650[-\s]/i.test(c)) { await redeemVoucher(c); return; }
    if (await resolve(c)) setCodeInput("");
  };
  const removeCode = () => { setResolved(null); clearReferrer(); setCodeErr(""); };

  const selfRefund = async (c) => {
    if (!window.confirm(`Refund "${c.title}"? Your payment is returned in full and course access is removed (available within 30 days of purchase).`)) return;
    try {
      const { data } = await api.post("/payments/self-refund", { course_id: c.id });
      toast.success(`Refunded ${`$${((data.refunded_cents || 0) / 100).toFixed(2)} ${settings?.currency || "CAD"}`} — it may take a few days to appear on your card`);
      const { data: cs } = await api.get("/courses", { params: lang !== "en" ? { lang } : {} });
      setCourses(cs);
    } catch (e) { setErr(formatApiError(e)); }
  };

  const buy = async (courseId) => {
    if (!canBuy) {
      setErr("Only public students can purchase. Switch to a student account.");
      return;
    }
    setBusy(courseId); setErr("");
    try {
      const { data } = await api.post("/payments/checkout", {
        course_id: courseId,
        origin_url: window.location.origin,
        code: getReferrerId() || undefined,
        referrer_captured_at: getReferrerCapturedAt() || undefined,
      });
      clearReferrer();
      window.location.href = data.url;
    } catch (e) { setErr(formatApiError(e)); setBusy(null); }
  };

  return (
    <div className="bg-grain min-h-screen">
      <div className="max-w-6xl mx-auto px-5 py-8">
        <button className="btn-ghost flex items-center gap-1 text-[#000000] mb-3" onClick={() => nav("/dashboard")} data-testid="catalog-back-button">
          <ArrowLeft size={16}/> Back to dashboard
        </button>
        <h1 className="text-4xl font-extrabold font-display">Course Catalog</h1>
        <p className="text-[#000000] mb-3">
          {isStaff
            ? "Every course is included with your school access. Pick a grade to experience it exactly as your students do."
            : "Secure checkout powered by Stripe. Lifetime access on purchase."}
        </p>

        {/* Code entry lives in the checkout dialog — not on the catalog page. */}

        {err && <div className="text-[#FF5100] mb-4">{err}</div>}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {courses.map((c) => (
            <motion.div key={c.id} whileHover={{ y: -3 }} className="card card-interactive flex flex-col gap-3" data-testid={`catalog-course-${c.id}`}>
              {c.cover_image_url && (
                <div className="relative -mx-6 -mt-6 h-36 overflow-hidden rounded-t-2xl">
                  <img src={c.cover_image_url} alt={c.title} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#FFFFFF] to-transparent" />
                </div>
              )}
              <div className="flex items-start justify-between gap-2">
                <h3 className="text-xl font-bold font-display">{c.title}</h3>
                {c.is_unlocked ? <CheckCircle size={22} weight="fill" color="#CCFF00" /> : <LockKey size={22} weight="duotone" color="#52525B" />}
              </div>
              <p className="text-sm text-[#000000] line-clamp-3">{c.description}</p>
              {(c.lesson_count > 0 || c.duration_minutes_total > 0) && (
                <div className="flex items-center gap-1.5 text-xs font-semibold text-[#000000]" data-testid={`catalog-runtime-${c.id}`}>
                  <Clock size={14} weight="bold" />
                  {c.lesson_count > 0 && <span>{c.lesson_count} lessons</span>}
                  {c.lesson_count > 0 && c.duration_minutes_total > 0 && <span>·</span>}
                  {c.duration_minutes_total > 0 && (
                    <span>{c.duration_minutes_total >= 60
                      ? `~${Math.round(c.duration_minutes_total / 60)} hour${Math.round(c.duration_minutes_total / 60) === 1 ? "" : "s"}`
                      : `${c.duration_minutes_total} min`} of training</span>
                  )}
                </div>
              )}
              <div className="flex flex-wrap gap-1.5">{(c.pillars || []).map(p => <span key={p} className="chip" style={pillarChipStyle(colorOf(p))}>{labelOf(p)}</span>)}</div>
              {isStaff ? (
                <div className="mt-2">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-lg font-extrabold font-display text-[#3f6212]" data-testid={`catalog-included-${c.id}`}>Included</span>
                    <span className="text-xs text-[#000000]">Take it as any grade:</span>
                  </div>
                  <div className="grid grid-cols-7 gap-1.5" data-testid={`grade-tiles-${c.id}`}>
                    {GRADES.map(g => {
                      const isLast = getLastGrade(user?.id, c.id) === g;
                      const label = g === "DEMO" ? "Demo" : g.slice(1);
                      const noun = g === "DEMO" ? "the evaluator Demo" : `Grade ${g.slice(1)}`;
                      return (
                        <button
                          key={g}
                          data-testid={`grade-tile-${c.id}-${g}`}
                          className={"rounded-lg border-2 py-2 font-display font-bold text-[#000000] hover:border-[#CCFF00] hover:bg-[rgba(204,255,0,0.15)] transition-colors " +
                            (g === "DEMO" ? "text-xs " : "text-sm ") +
                            (isLast ? "border-[#CCFF00] bg-[rgba(204,255,0,0.25)]" : "border-[#C4C8CE] bg-white")}
                          title={isLast ? `Your last pick — ${noun}` : `Open this course as ${noun}`}
                          onClick={() => { setLastGrade(user?.id, c.id, g); nav(`/course/${c.id}?grade=${g}`); }}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : (
              <div className="flex items-center justify-between gap-3 mt-2">
                <div className="flex flex-col">
                  {c.price_cents ? (
                    (discount > 0 || discountCents > 0) && !c.is_unlocked ? (
                      <>
                        <span className="text-xs text-[#94979C] line-through" data-testid={`catalog-list-price-${c.id}`}>{money(c.price_cents)}</span>
                        <span className="text-2xl font-extrabold font-display text-[#000000]" data-testid={`catalog-price-${c.id}`}>{money(priced(c.price_cents))}</span>
                      </>
                    ) : (
                      <span className="text-2xl font-extrabold font-display text-[#000000]" data-testid={`catalog-price-${c.id}`}>{money(c.price_cents)}</span>
                    )
                  ) : null}
                </div>
                {c.is_unlocked ? (
                  <div className="flex flex-col items-end gap-0.5">
                    <button className="btn-primary text-sm" onClick={() => nav(`/course/${c.id}`)} data-testid={`catalog-open-${c.id}`}>Open</button>
                    {canBuy && c.price_cents > 0 && (
                      <button className="text-[10px] underline underline-offset-2 text-[#94979C] hover:text-white bg-transparent border-0 cursor-pointer p-0"
                        onClick={() => selfRefund(c)} data-testid={`catalog-refund-${c.id}`}
                        title="Full refund within 30 days of purchase">request refund</button>
                    )}
                  </div>
                ) : canBuy ? (
                  <div className="flex items-center gap-2">
                    <button className="btn-secondary text-sm" onClick={() => nav(`/course/${c.id}`)} data-testid={`catalog-preview-${c.id}`} title="Watch the first 5 lessons free — no payment needed">Start for free</button>
                    <button className="btn-primary text-sm flex items-center gap-1" onClick={() => { setErr(""); setCodeErr(""); setCheckoutFor(c); }} disabled={busy === c.id} data-testid={`catalog-buy-${c.id}`}>
                      {busy === c.id ? <Spinner size={16} className="animate-spin" /> : <ShoppingCart size={14}/>} {busy === c.id ? "Loading..." : "Buy"}
                    </button>
                  </div>
                ) : (
                  <button className="btn-secondary text-sm" disabled title="Sign in as a public student to purchase">Student-only</button>
                )}
              </div>
              )}
            </motion.div>
          ))}
        </div>

        {/* Pre-checkout dialog: price summary + promo/referral code entry */}
        <Dialog open={!!checkoutFor} onOpenChange={(o) => { if (!o) setCheckoutFor(null); }}>
          <DialogContent className="max-w-md" data-testid="checkout-dialog">
            <DialogHeader>
              <DialogTitle className="font-display">Checkout</DialogTitle>
              <DialogDescription>Secure payment powered by Stripe. Lifetime access on purchase.</DialogDescription>
            </DialogHeader>
            {checkoutFor && (
              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="font-bold font-display">{checkoutFor.title}</div>
                  <div className="text-right shrink-0">
                    {(discount > 0 || discountCents > 0) ? (
                      <>
                        <div className="text-xs text-[#94979C] line-through" data-testid="checkout-list-price">{money(checkoutFor.price_cents)}</div>
                        <div className="text-2xl font-extrabold font-display text-[#3f6212]" data-testid="checkout-price">{money(priced(checkoutFor.price_cents))}</div>
                      </>
                    ) : (
                      <div className="text-2xl font-extrabold font-display" data-testid="checkout-price">{money(checkoutFor.price_cents)}</div>
                    )}
                  </div>
                </div>
                {resolved?.valid ? (
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[rgba(204,255,0,0.12)] border border-[rgba(204,255,0,0.4)] text-[#3f6212] text-xs font-display font-semibold self-start" data-testid="referral-attribution-badge">
                    <Tag size={14} weight="fill" />
                    {(discount > 0 || discountCents > 0)
                      ? <>Code <b>{resolved.code}</b> applied — <b>{discount > 0 ? `${Math.round(discount)}% off` : `$${discountCents % 100 ? (discountCents / 100).toFixed(2) : discountCents / 100} off`}</b></>
                      : <>Referred by {resolved.instructor_name} · code <b>{resolved.code}</b></>}
                    <button data-testid="remove-code-button" onClick={removeCode} className="ml-1 opacity-70 hover:opacity-100"><X size={13} weight="bold" /></button>
                  </div>
                ) : (
                  <form onSubmit={applyCode} className="flex items-center gap-2" data-testid="code-entry-form">
                    <input data-testid="code-input" className="input-base flex-1" placeholder="Promo or access code (e.g. E650-ABC12)" value={codeInput} onChange={(e) => setCodeInput(e.target.value)} />
                    <button data-testid="apply-code-button" type="submit" className="btn-secondary whitespace-nowrap">Apply</button>
                  </form>
                )}
                {codeErr && <div className="text-[#FF5100] text-sm" data-testid="code-error">{codeErr}</div>}
                {err && <div className="text-[#FF5100] text-sm" data-testid="checkout-error">{err}</div>}
                <button className="btn-primary w-full flex items-center justify-center gap-2" onClick={() => buy(checkoutFor.id)} disabled={busy === checkoutFor.id} data-testid="checkout-proceed-button">
                  {busy === checkoutFor.id ? <Spinner size={16} className="animate-spin" /> : <ShoppingCart size={15} />} {busy === checkoutFor.id ? "Loading..." : "Proceed to secure checkout"}
                </button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
